// The tab's workspace, kept in this browser.
//
// A workspace belongs to one browser tab: a new tab starts empty. What the
// server sends when files are opened (asset lists, previews, dumps, texture
// options) and the results of replacements are kept here, in IndexedDB, so
// previews never need the server again and a reload of the tab picks up where
// it was. The server forgets its copy of the files after an hour idle; the tab
// keeps showing its work and sends the files again when it needs the server
// (the replacement files and, where the browser allows, handles to the opened
// files are kept here for that). When IndexedDB is not available (some private
// modes) everything stays in memory for this page.

const DB_NAME = "abvme";
const DB_VERSION = 3;
/** Matches the server's SESSION_IDLE_MINUTES: after this the server's copy is gone. */
export const IDLE_MS = 60 * 60 * 1000;
/** Work of other tabs (closed, or not reopened) is dropped after this long unused. */
const KEEP_MS = 7 * 24 * 60 * 60 * 1000;
const TAB_KEY = "abvme_tab";

// Stores: workspaces {tab, ws, name, files, edits, saved_at, last_active}
//         items {key: `${tab}|${fid}|${pid}|${sig}`, tab, preview, dump, texture_options, image (Blob)}
//         assets {key: `${tab}|${fid}`, tab, rows}
//         edits {key: `${tab}|${fid}|${pid}`, tab, blob, name, options, created_at}: replacement files
//         handles {key: `${tab}|${fid}`, tab, handle}: FileSystemFileHandle of an opened file
//         recent {id, owner, files: [{name, size, handle? | blob?}], folder?, opened_at}: File > Open Recent. Not
//                kept per tab: it is the device's list, so it outlives the tabs and is not swept.
let dbPromise = null;

function openDb() {
  dbPromise ??= new Promise((resolve) => {
    let req;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      const have = new Set(db.objectStoreNames);
      if (!have.has("workspaces")) db.createObjectStore("workspaces", { keyPath: "tab" });
      if (!have.has("recent")) db.createObjectStore("recent", { keyPath: "id" });
      for (const name of TAB_STORES) if (!have.has(name)) db.createObjectStore(name, { keyPath: "key" }).createIndex("tab", "tab");
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
    req.onblocked = () => resolve(null);
  });
  return dbPromise;
}

const done = (req) =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

/** Stores keyed per tab, cleared with the tab. */
const TAB_STORES = ["assets", "items", "edits", "handles"];

const finished = (tx) =>
  new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error("Storage transaction aborted"));
  });

/** This tab's id (sessionStorage is per tab and survives reloads). */
export function tabId() {
  let id = null;
  try {
    id = sessionStorage.getItem(TAB_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(TAB_KEY, id);
    }
  } catch {
    id ??= crypto.randomUUID();
  }
  return id;
}

/** In-memory fallback with the same shape as the stores. */
const memory = { workspaces: new Map(), assets: new Map(), items: new Map(), edits: new Map(), handles: new Map(), recent: new Map() };

async function put(store, value) {
  const db = await openDb();
  if (!db) {
    memory[store].set(store === "workspaces" ? value.tab : store === "recent" ? value.id : value.key, value);
    return;
  }
  const tx = db.transaction(store, "readwrite");
  tx.objectStore(store).put(value);
  await finished(tx);
}

async function get(store, key) {
  const db = await openDb();
  if (!db) return memory[store].get(key) ?? null;
  return (await done(db.transaction(store).objectStore(store).get(key))) ?? null;
}

/** Delete a tab's workspace and everything kept for it. */
export async function clearTab(tab) {
  const db = await openDb();
  if (!db) {
    memory.workspaces.delete(tab);
    for (const s of TAB_STORES) for (const [k, v] of memory[s]) if (v.tab === tab) memory[s].delete(k);
    return;
  }
  const tx = db.transaction(["workspaces", ...TAB_STORES], "readwrite");
  tx.objectStore("workspaces").delete(tab);
  for (const s of TAB_STORES) {
    const range = IDBKeyRange.only(tab);
    const index = tx.objectStore(s).index("tab");
    index.openKeyCursor(range).onsuccess = (e) => {
      const cursor = e.target.result;
      if (!cursor) return;
      tx.objectStore(s).delete(cursor.primaryKey);
      cursor.continue();
    };
  }
  await finished(tx);
}

/** Drop the work of other tabs (closed, or never reopened) unused for a week. */
export async function sweepIdle(keepTab) {
  const db = await openDb();
  const all = db ? await done(db.transaction("workspaces").objectStore("workspaces").getAll()) : [...memory.workspaces.values()];
  const cutoff = Date.now() - KEEP_MS;
  for (const w of all) if (w.tab !== keepTab && (w.last_active ?? 0) < cutoff) await clearTab(w.tab).catch(() => {});
}

// ----- workspace record ----------------------------------------------------

export const loadWorkspaceRecord = (tab) => get("workspaces", tab);

export function saveWorkspaceRecord(record) {
  return put("workspaces", { ...record, last_active: record.last_active ?? Date.now() });
}

export async function touchWorkspaceRecord(tab) {
  const rec = await get("workspaces", tab);
  if (rec) await put("workspaces", { ...rec, last_active: Date.now() });
}

// ----- assets and items ------------------------------------------------------

export const putAssets = (tab, fileId, rows) => put("assets", { key: `${tab}|${fileId}`, tab, rows });
export async function getAssets(tab, fileId) {
  return (await get("assets", `${tab}|${fileId}`))?.rows ?? null;
}

/** sig: "orig" or the edit's created_at. */
const itemKey = (tab, fileId, pid, sig) => `${tab}|${fileId}|${pid}|${sig}`;

export async function putItems(tab, fileId, sig, items) {
  const db = await openDb();
  const values = items.map(([pid, item]) => ({ key: itemKey(tab, fileId, pid, sig), tab, ...item }));
  if (!db) {
    for (const v of values) memory.items.set(v.key, v);
    return;
  }
  // One transaction per chunk: a file can hold thousands of assets.
  for (let i = 0; i < values.length; i += 500) {
    const tx = db.transaction("items", "readwrite");
    const store = tx.objectStore("items");
    for (const v of values.slice(i, i + 500)) store.put(v);
    await finished(tx);
  }
}

export const getItem = (tab, fileId, pid, sig) => get("items", itemKey(tab, fileId, pid, sig));

export async function deleteItem(tab, fileId, pid, sig) {
  const db = await openDb();
  const key = itemKey(tab, fileId, pid, sig);
  if (!db) {
    memory.items.delete(key);
    return;
  }
  const tx = db.transaction("items", "readwrite");
  tx.objectStore("items").delete(key);
  await finished(tx);
}

async function del(store, key) {
  const db = await openDb();
  if (!db) {
    memory[store].delete(key);
    return;
  }
  const tx = db.transaction(store, "readwrite");
  tx.objectStore(store).delete(key);
  await finished(tx);
}

// ----- what is needed to send the work to the server again ---------------

/** A replacement file, kept so it can be sent again after the server's copy expired. */
export const putEditFile = (tab, fileId, pid, { blob, name, options, created_at }) =>
  put("edits", { key: `${tab}|${fileId}|${pid}`, tab, blob, name, options: options ?? null, created_at });
export const getEditFile = (tab, fileId, pid) => get("edits", `${tab}|${fileId}|${pid}`);
export const deleteEditFile = (tab, fileId, pid) => del("edits", `${tab}|${fileId}|${pid}`);

/** A FileSystemFileHandle of an opened file (Chromium): read again after a reload, with permission. */
export const putHandle = (tab, fileId, handle) => put("handles", { key: `${tab}|${fileId}`, tab, handle });
export async function getHandle(tab, fileId) {
  return (await get("handles", `${tab}|${fileId}`))?.handle ?? null;
}

// ----- recently opened files (File > Open Recent) --------------------------

/** How many entries the list keeps per owner. */
export const RECENT_MAX = 10;

/** The most the stored copies of files may take together, so the list cannot fill a phone. */
export const RECENT_COPY_MAX_BYTES = 500 * 1024 * 1024;

/** Bytes of the file copies an entry holds (files read again from a handle take none). */
export const recentCopyBytes = (entry) => entry.files.reduce((n, f) => n + (f.blob ? f.size : 0), 0);

/**
 * Remember a set of opened files, most recent first. `files` are {name, size}
 * plus either `handle` (a FileSystemFileHandle: read again from disk) or `blob`
 * (a copy of the file, for browsers without handles such as phones); `folder`
 * is the directory handle they were picked from, if any (one permission then
 * covers all of them). The same files opened again move to the top instead of
 * adding an entry. Older entries go when the list is full or the copies would
 * pass RECENT_COPY_MAX_BYTES. Returns false when nothing could be stored (too
 * big, or the browser's storage is full).
 */
export async function addRecent(owner, { files, folder = null }) {
  const sig = files.map((f) => `${f.name}:${f.size}`).sort().join("|");
  const entry = { id: `${owner}|${sig}`, owner, files, folder, opened_at: Date.now() };
  const own = recentCopyBytes(entry);
  if (own > RECENT_COPY_MAX_BYTES) return false;
  let bytes = own;
  let count = 1;
  for (const old of (await listRecent(owner)).filter((r) => r.id !== entry.id)) {
    bytes += recentCopyBytes(old);
    if (++count > RECENT_MAX || bytes > RECENT_COPY_MAX_BYTES) await del("recent", old.id);
  }
  try {
    await put("recent", entry);
  } catch {
    // The browser's storage is full: keep only this entry and try once more.
    await clearRecent(owner).catch(() => {});
    try {
      await put("recent", entry);
    } catch {
      return false;
    }
  }
  return true;
}

/** An entry opened again: move it to the top without writing its files once more. */
export async function touchRecent(id) {
  const entry = await get("recent", id);
  if (entry) await put("recent", { ...entry, opened_at: Date.now() });
}

/** The owner's entries, newest first. */
export async function listRecent(owner) {
  const db = await openDb();
  const all = db ? await done(db.transaction("recent").objectStore("recent").getAll()) : [...memory.recent.values()];
  return all.filter((r) => r.owner === owner).sort((a, b) => b.opened_at - a.opened_at);
}

export const removeRecent = (id) => del("recent", id);

export async function clearRecent(owner) {
  for (const entry of await listRecent(owner)) await del("recent", entry.id);
}

/** Whether the browser keeps data on disk (false = memory only, lost on reload). */
export async function persistent() {
  return Boolean(await openDb());
}
